import { useMutation } from '@apollo/client';
import type { DeleteUserGroupsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteUserGroupsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteUserGroupsTRPC } from './useDeleteUserGroupsTRPC';

type DeleteUserGroupsInput = {
  UserGroupIds: string[];
};

export const useDeleteUserGroups = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [deleteUserGroupsGraphQL, graphqlState] = useMutation(
    DeleteUserGroupsDocument,
    {
      update: (cache) => {
        evictField(cache, 'user_group');
        evictField(cache, 'user_group_user');
      },
    }
  );

  const trpcMutation = useDeleteUserGroupsTRPC();

  const deleteUserGroups = async (
    variables: DeleteUserGroupsInput
  ): Promise<DeleteUserGroupsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteUserGroups(variables);
    }

    const result = await deleteUserGroupsGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to delete user groups');
    }

    return result.data;
  };

  return {
    deleteUserGroups,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
